import { Box } from "@samdesk/components/Box";
import { Divider } from "@samdesk/components/Divider";
import { Heading } from "@samdesk/components/Heading";
import { Link } from "@samdesk/components/Link";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import samdeskLogo from "images/samdeskFontLogo.png";
import { withErrorBoundary } from "utils/ErrorBoundary";

const TermsOfService = () => {
	return (
		<div style={scrollWrapperStyle}>
			<Stack direction="column" spacing="none">
				<Box spacingHorizontal="larger" spacingVertical="base">
					<a href="/">
						<img alt="samdesk" src={samdeskLogo} style={logoStyle} />
					</a>
				</Box>
				<Divider />
				<Box spacing="largest" style={{ maxWidth: "800px", margin: "0 auto" }}>
					<Stack direction="column" spacing="large">
						<Heading level="1">Terms of Service</Heading>
						<Text prominence="secondary">Effective Date: May 2021</Text>
						<Text prominence="secondary">Last updated: May 2026</Text>

						<Heading level="3">Introduction</Heading>
						<Text>
							Welcome to <Text weight="bold">Samdesk Canada Inc.</Text> (
							<Text weight="bold">"Company"</Text>,{" "}
							<Text weight="bold">"we"</Text>, <Text weight="bold">"our"</Text>,{" "}
							<Text weight="bold">"us"</Text>)! As you have just clicked our
							Terms of Service, please pause, grab a cup of coffee and carefully
							read the following pages. It will take you approximately 20
							minutes.
						</Text>
						<Text>
							These <Text weight="bold">Terms of Service</Text> (
							<Text weight="bold">"Terms"</Text>,{" "}
							<Text weight="bold">"Terms of Service"</Text>) govern your use of
							our web and mobile applications accessed at dash.samdesk.io,
							downloaded from third party application marketplaces, or accessed
							via our Application Programming Interface (API), (together or
							individually
							<Text weight="bold">”Service”</Text>) operated by Samdesk Canada
							Inc.
						</Text>
						<Text>
							Our Privacy Policy also governs your use of our Service and
							explains how we collect, safeguard and disclose information that
							results from your use of our web pages. Please read it here{" "}
							<Link href="https://dash.samdesk.io/privacy">
								https://dash.samdesk.io/privacy
							</Link>
							. For information regarding our Privacy Policy for our website
							hosted at www.samdesk.io please see{" "}
							<Link href="https://samdesk.io/privacy">
								https://samdesk.io/privacy
							</Link>
						</Text>
						<Text>
							Your agreement with us includes these Terms and our Privacy Policy
							(<Text weight="bold">"Agreements"</Text>). You acknowledge that
							you have read and understood Agreements, and agree to be bound by
							them.
						</Text>
						<Text>
							If you do not agree with (or cannot comply with) these Agreements,
							then you may not use the Service, but please let us know by
							emailing at{" "}
							<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link> so we can
							try to find a solution. These Terms apply to all visitors, users
							and others who wish to access or use the Service.
						</Text>
						<Text>Thank you for being responsible.</Text>

						<Heading level="3">Communications</Heading>
						<Text>
							By creating an Account on our Service, you agree to subscribe to
							newsletters, marketing or promotional materials and other
							information we may send. However, you may opt out of receiving
							any, or all, of these communications from us by following the
							unsubscribe link or by emailing at{" "}
							<Link href="mailto:admin@samdesk.io">admin@samdesk.io</Link>.
						</Text>

						<Heading level="3">Subscriptions</Heading>
						<Text>
							Details of your subscription, including Billing, Term, and Service
							Level Agreements, are outlined in you or your organizations Order
							Form and Standard Terms and Conditions agreements.
						</Text>

						<Heading level="3">Content</Heading>
						<Text>
							Samdesk cannot review all of the material curated via the Service,
							and cannot therefore be responsible for that material’s content,
							use or effects. By operating the Service, samdesk does not
							represent or imply that it endorses the material curated, or that
							it believes such material to be accurate or non-harmful. You are
							responsible for taking precautions as necessary to protect
							yourself of potentially inaccurate or damaging content. The
							Service may contain content that is offensive, contains errors or
							inaccuracies, violates the privacy or publicity rights, infringes
							the intellectual property and other proprietary rights of third
							parties, or the downloading, copying or use of which is subject to
							additional terms and conditions, stated or unstated. Samdesk
							disclaims any responsibility for any harm resulting from the use
							by users of the Service, or from any downloading or viewing by
							those visitors of content there posted.
						</Text>
						<Text>
							We have not reviewed all of the material, including computer
							software, made available through the Services, outputs, 3rd party
							integrations or data connectors that link to samdesk. Samdesk does
							not have any control over any non-samdesk Web Applications,
							integrations and webpages, and is not responsible for their
							contents or their use. By linking to a non-samdesk Web
							Application, integration or webpage, samdesk does not represent or
							imply that it endorses such Web Application or webpage. You are
							responsible for taking precautions as necessary to protect
							yourself and your computer systems from viruses, worms, Trojan
							horses, and other harmful or destructive content. Samdesk
							disclaims any responsibility for any harm resulting from your use
							of non-samdesk Web Applications, integrations and webpages.
						</Text>

						<Heading level="3">Prohibited Uses</Heading>
						<Text>
							You may use Service only for lawful purposes and in accordance
							with Terms. You agree not to use Service:
						</Text>
						<ol>
							<li>
								<Text>
									In any way that violates any applicable national or
									international law or regulation.
								</Text>
							</li>
							<li>
								<Text>
									For the purpose of exploiting, harming, or attempting to
									exploit or harm minors in any way by exposing them to
									inappropriate content or otherwise.
								</Text>
							</li>
							<li>
								<Text>
									To transmit, or procure the sending of, any advertising or
									promotional material, including any “junk mail”, “chain
									letter,” “spam,” or any other similar solicitation.
								</Text>
							</li>
							<li>
								<Text>
									To impersonate or attempt to impersonate Company, a Company
									employee, another user, or any other person or entity.
								</Text>
							</li>
							<li>
								<Text>
									In any way that infringes upon the rights of others, or in any
									way is illegal, threatening, fraudulent, or harmful, or in
									connection with any unlawful, illegal, fraudulent, or harmful
									purpose or activity.
								</Text>
							</li>
							<li>
								<Text>
									To engage in any other conduct that restricts or inhibits
									anyone’s use or enjoyment of Service, or which, as determined
									by us, may harm or offend Company or users of Service or
									expose them to liability.
								</Text>
							</li>
							<li>
								<Text>
									To surveil, attempt to identify, track, locate, or derive
									private data or information unrelated to the information
									provided by Company of any individual user or entity contained
									or referenced therein;
								</Text>
							</li>
							<li>
								<Text>
									To perform any activities which are illegal, discriminatory,
									restricted by this Agreement, or otherwise violates the
									reasonable expectation of privacy and anonymity by users or
									entities contained or referenced therein.
								</Text>
							</li>
							<li>
								<Text>
									To syndicate or otherwise programmatically disseminate the
									Services to third parties, directly or indirectly, without the
									prior express permission of Company.
								</Text>
							</li>
							<li>
								<Text>
									In any manner that could disable, overburden, damage, or
									impair Service or interfere with any other party’s use of
									Service, including their ability to engage in real time
									activities through Service.
								</Text>
							</li>
						</ol>
						<Text>In addition, you agree not to:</Text>
						<ol>
							<li>
								<Text>
									Use any robot, spider, or other automatic device, process, or
									means to access Service for any purpose, including monitoring
									or copying any of the material on Service.
								</Text>
							</li>
							<li>
								<Text>
									Attempt to damage, deny service to, hack, crack, reverse
									engineer or otherwise interfere with the Site in any manner.
								</Text>
							</li>
							<li>
								<Text>
									Use any device, software, or routine that interferes with the
									proper working of Service.
								</Text>
							</li>
							<li>
								<Text>
									Introduce any viruses, trojan horses, worms, logic bombs, or
									other material which is malicious or technologically harmful.
								</Text>
							</li>
							<li>
								<Text>
									Attempt to gain unauthorized access to, interfere with,
									damage, or disrupt any parts of Service, the server on which
									Service is stored, or any server, computer, or database
									connected to Service.
								</Text>
							</li>
							<li>
								<Text>
									Attack Service via a denial-of-service attack or a distributed
									denial-of-service attack.
								</Text>
							</li>
							<li>
								<Text>
									Take any action that may damage or falsify Company rating.
								</Text>
							</li>
							<li>
								<Text>
									Reverse engineer, decompile, disassemble or otherwise attempt
									to discover the source code, object code or underlying
									structure, ideas, know-how or algorithms relevant to the
									Services or
								</Text>
							</li>
							<li>
								<Text>
									Modify, translate, or create derivative works based on the
									Services (except to the extent expressly permitted by Company
									or authorized within the Services)
								</Text>
							</li>
							<li>
								<Text>
									Otherwise attempt to interfere with the proper working of
									Service.
								</Text>
							</li>
						</ol>

						<Heading level="3">No Use By Minors</Heading>
						<Text>
							Service is intended only for access and use by individuals at
							least eighteen (18) years old. By accessing or using any of
							Company, you warrant and represent that you are at least eighteen
							(18) years of age and with the full authority, right, and capacity
							to enter into this agreement and abide by all of the terms and
							conditions of Terms. If you are not at least eighteen (18) years
							old, you are prohibited from both the access and usage of Service.
						</Text>

						<Heading level="3">Accounts</Heading>
						<Text>
							When you create an account with us, you guarantee that you are
							above the age of 18, and that the information you provide us is
							accurate, complete, and current at all times. Inaccurate,
							incomplete, or obsolete information may result in the immediate
							termination of your account on Service.
						</Text>
						<Text>
							You are responsible for maintaining the confidentiality of your
							account and password, including but not limited to the restriction
							of access to your computer and/or account. You agree to accept
							responsibility for any and all activities or actions that occur
							under your account and/or password, whether your password is with
							our Service or a third-party service. You must notify us
							immediately upon becoming aware of any breach of security or
							unauthorized use of your account.
						</Text>
						<Text>
							You may not use as a username the name of another person or entity
							or that is not lawfully available for use, a name or trademark
							that is subject to any rights of another person or entity other
							than you, without appropriate authorization. You may not use as a
							username any name that is offensive, vulgar or obscene.
						</Text>
						<Text>
							We reserve the right to refuse service, terminate accounts,or
							remove or edit content, in our sole discretion.
						</Text>

						<Heading level="3">Intellectual Property</Heading>
						<Text>
							Service and its original content (excluding Content provided by
							users or third party data providers), features and functionality
							are and will remain the exclusive property of Samdesk Canada Inc.
							and its licensors. Service is protected by copyright, trademark,
							and other laws of Canada, the United States and foreign countries.
							Our trademarks and trade dress may not be used in connection with
							any product or service without the prior written consent of
							Samdesk Canada Inc.
						</Text>

						<Heading level="3">Copyright Policy</Heading>
						<Text>
							We respect the intellectual property rights of others. It is our
							policy to respond to any claim that Content posted on Service
							infringes on the copyright or other intellectual property rights (
							<Text weight="bold">"Infringement"</Text>) of any person or
							entity.
						</Text>
						<Text>
							If you are a copyright owner, or authorized on behalf of one, and
							you believe that the copyrighted work has been copied in a way
							that constitutes copyright infringement, please submit your claim
							via email to{" "}
							<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>, with the
							subject line: “Copyright Infringement” and include in your claim a
							detailed description of the alleged Infringement as detailed
							below, under “DMCA Notice and Procedure for Copyright Infringement
							Claims”
						</Text>
						<Text>
							You may be held accountable for damages (including costs and
							attorneys' fees) for misrepresentation or bad-faith claims on the
							infringement of any Content found on and/or through Service on
							your copyright.
						</Text>

						<Heading level="3">
							DMCA Notice and Procedure for Copyright Infringement Claims
						</Heading>
						<Text>
							You may submit a notification pursuant to the Digital Millennium
							Copyright Act (DMCA) by providing our Copyright Agent with the
							following information in writing (see 17 U.S.C 512(c)(3) for
							further detail):
						</Text>
						<ol>
							<li>
								<Text>
									an electronic or physical signature of the person authorized
									to act on behalf of the owner of the copyright's interest;
								</Text>
							</li>
							<li>
								<Text>
									a description of the copyrighted work that you claim has been
									infringed, including the URL (i.e., web page address) of the
									location where the copyrighted work exists or a copy of the
									copyrighted work;
								</Text>
							</li>
							<li>
								<Text>
									identification of the URL or other specific location on
									Service where the material that you claim is infringing is
									located;
								</Text>
							</li>
							<li>
								<Text>your address, telephone number, and email address;</Text>
							</li>
							<li>
								<Text>
									a statement by you that you have a good faith belief that the
									disputed use is not authorized by the copyright owner, its
									agent, or the law;
								</Text>
							</li>
							<li>
								<Text>
									a statement by you, made under penalty of perjury, that the
									above information in your notice is accurate and that you are
									the copyright owner or authorized to act on the copyright
									owner's behalf.
								</Text>
							</li>
						</ol>
						<Text>
							You can contact our Copyright Agent via email at{" "}
							<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>.
						</Text>

						<Heading level="3">Error Reporting and Feedback</Heading>
						<Text>
							You may provide us directly at{" "}
							<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link> with
							information and feedback concerning errors, suggestions for
							improvements, ideas, problems, complaints, and other matters
							related to our Service (<Text weight="bold">"Feedback"</Text>).
							You acknowledge and agree that: (i) you shall not retain, acquire
							or assert any intellectual property right or other right, title or
							interest in or to the Feedback; (ii) Company may have development
							ideas similar to the Feedback; (iii) Feedback does not contain
							confidential information or proprietary information from you or
							any third party; and (iv) Company is not under any obligation of
							confidentiality with respect to the Feedback. In the event the
							transfer of the ownership to the Feedback is not possible due to
							applicable mandatory laws, you grant Company and its affiliates an
							exclusive, transferable, irrevocable, free-of-charge,
							sub-licensable, unlimited and perpetual right to use (including
							copy, modify, create derivative works, publish, distribute and
							commercialize) Feedback in any manner and for any purpose.
						</Text>

						<Heading level="3">Links To Other Web Sites</Heading>
						<Text>
							Our Service may contain links to third party web sites or services
							that are not owned or controlled by Samdesk Canada Inc.
						</Text>
						<Text>
							Samdesk Canada Inc. has no control over, and assumes no
							responsibility for the content, privacy policies, or practices of
							any third party web sites or services. We do not warrant the
							offerings of any of these entities/individuals or their websites.
							Further, third party content which may be access through our
							Service is subject to the terms, conditions, and policies of
							respective content providers and it is your responsibility to
							obtain any and all relevant permissions or licenses which may
							apply to third party content prior to use, modification,
							reproduction, distribution, display, or transmission.
						</Text>
						<Text>
							YOU ACKNOWLEDGE AND AGREE THAT SAMDESK CANADA INC. SHALL NOT BE
							RESPONSIBLE OR LIABLE, DIRECTLY OR INDIRECTLY, FOR ANY DAMAGE OR
							LOSS CAUSED OR ALLEGED TO BE CAUSED BY OR IN CONNECTION WITH USE
							OF OR RELIANCE ON ANY SUCH CONTENT, GOODS OR SERVICES AVAILABLE ON
							OR THROUGH ANY SUCH THIRD PARTY WEB SITES OR SERVICES.
						</Text>
						<Text>
							WE STRONGLY ADVISE YOU TO READ THE TERMS OF SERVICE AND PRIVACY
							POLICIES OF ANY THIRD PARTY WEB SITES OR SERVICES THAT YOU VISIT.
						</Text>

						<Heading level="3">Disclaimer Of Warranty</Heading>
						<Text>
							THESE SERVICES ARE PROVIDED BY COMPANY ON AN “AS IS” AND “AS
							AVAILABLE” BASIS. COMPANY MAKES NO REPRESENTATIONS OR WARRANTIES
							OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THEIR
							SERVICES, OR THE INFORMATION, CONTENT OR MATERIALS INCLUDED
							THEREIN. YOU EXPRESSLY AGREE THAT YOUR USE OF THESE SERVICES,
							THEIR CONTENT, AND ANY SERVICES OR ITEMS OBTAINED FROM US IS AT
							YOUR SOLE RISK.
						</Text>
						<Text>
							NEITHER COMPANY NOR ANY PERSON ASSOCIATED WITH COMPANY MAKES ANY
							WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS,
							SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE
							SERVICES. WITHOUT LIMITING THE FOREGOING, NEITHER COMPANY NOR
							ANYONE ASSOCIATED WITH COMPANY REPRESENTS OR WARRANTS THAT THE
							SERVICES, THEIR CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH
							THE SERVICES WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR
							UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT THE SERVICES
							OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER
							HARMFUL COMPONENTS OR THAT THE SERVICES OR ANY SERVICES OR ITEMS
							OBTAINED THROUGH THE SERVICES WILL OTHERWISE MEET YOUR NEEDS OR
							EXPECTATIONS.
						</Text>
						<Text>
							COMPANY HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER
							EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT
							LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT,
							AND FITNESS FOR PARTICULAR PURPOSE.
						</Text>
						<Text>
							THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH CANNOT BE
							EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
						</Text>

						<Heading level="3">Limitation Of Liability</Heading>
						<Text>
							EXCEPT AS PROHIBITED BY LAW, YOU WILL HOLD US AND OUR OFFICERS,
							DIRECTORS, EMPLOYEES, AND AGENTS HARMLESS FOR ANY INDIRECT,
							PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGE, HOWEVER IT
							ARISES (INCLUDING ATTORNEYS' FEES AND ALL RELATED COSTS AND
							EXPENSES OF LITIGATION AND ARBITRATION, OR AT TRIAL OR ON APPEAL,
							IF ANY, WHETHER OR NOT LITIGATION OR ARBITRATION IS INSTITUTED),
							WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE, OR OTHER TORTIOUS
							ACTION, OR ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT,
							INCLUDING WITHOUT LIMITATION ANY CLAIM FOR PERSONAL INJURY OR
							PROPERTY DAMAGE, ARISING FROM THIS AGREEMENT AND ANY VIOLATION BY
							YOU OF ANY FEDERAL, STATE, OR LOCAL LAWS, STATUTES, RULES, OR
							REGULATIONS, EVEN IF COMPANY HAS BEEN PREVIOUSLY ADVISED OF THE
							POSSIBILITY OF SUCH DAMAGE. EXCEPT AS PROHIBITED BY LAW, IF THERE
							IS LIABILITY FOUND ON THE PART OF COMPANY, IT WILL BE LIMITED TO
							THE AMOUNT PAID FOR THE PRODUCTS AND/OR SERVICES, AND UNDER NO
							CIRCUMSTANCES WILL THERE BE CONSEQUENTIAL OR PUNITIVE DAMAGES.
							SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF PUNITIVE,
							INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE PRIOR LIMITATION OR
							EXCLUSION MAY NOT APPLY TO YOU.
						</Text>

						<Heading level="3">Governing Law</Heading>
						<Text>
							These Terms shall be governed and construed in accordance with the
							laws of Alberta, Canada without regard to its conflict of law
							provisions.
						</Text>
						<Text>
							Our failure to enforce any right or provision of these Terms will
							not be considered a waiver of those rights. If any provision of
							these Terms is held to be invalid or unenforceable by a court, the
							remaining provisions of these Terms will remain in effect. These
							Terms constitute the entire agreement between us regarding our
							Service and supersede and replace any prior agreements we might
							have had between us regarding Service.
						</Text>

						<Heading level="3">Changes To Service</Heading>
						<Text>
							We reserve the right to withdraw or amend our Service, and any
							service or material we provide via Service, in our sole discretion
							without notice, or as otherwise agreed to in our Standard Terms
							and Conditions agreement with your organization. We will not be
							liable if for any reason all or any part of Service is unavailable
							at any time or for any period, except as what may be outlined in
							the Services Level Agreement with your organization. From time to
							time, we may restrict access to some parts of Service, or the
							entire Service, to users, including registered users.
						</Text>

						<Heading level="3">Amendments To Terms</Heading>
						<Text>
							We may amend Terms at any time by posting the amended terms on
							this site. It is your responsibility to review these Terms
							periodically.
						</Text>
						<Text>
							Your continued use of the Platform following the posting of
							revised Terms means that you accept and agree to the changes. You
							are expected to check this page frequently so you are aware of any
							changes, as they are binding on you.
						</Text>
						<Text>
							By continuing to access or use our Service after any revisions
							become effective, you agree to be bound by the revised terms. If
							you do not agree to the new terms, you are no longer authorized to
							use Service.
						</Text>

						<Heading level="3">Waiver And Severability</Heading>
						<Text>
							No waiver by Company of any term or condition set forth in Terms
							shall be deemed a further or continuing waiver of such term or
							condition or a waiver of any other term or condition, and any
							failure of Company to assert a right or provision under Terms
							shall not constitute a waiver of such right or provision.
						</Text>
						<Text>
							If any provision of Terms is held by a court or other tribunal of
							competent jurisdiction to be invalid, illegal or unenforceable for
							any reason, such provision shall be eliminated or limited to the
							minimum extent such that the remaining provisions of Terms will
							continue in full force and effect.
						</Text>

						<Heading level="3">Acknowledgement</Heading>
						<Text>
							BY USING SERVICE OR OTHER SERVICES PROVIDED BY US, YOU ACKNOWLEDGE
							THAT YOU HAVE READ THESE TERMS OF SERVICE AND AGREE TO BE BOUND BY
							THEM.
						</Text>

						<Heading level="3">Contact Us</Heading>
						<Text>
							Please send your feedback, comments, requests for technical
							support:
						</Text>
						<Text>
							By email: <Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>
							.
						</Text>
					</Stack>
				</Box>
			</Stack>
		</div>
	);
};

const logoStyle = {
	height: "20px",
	width: "auto",
	display: "block",
};

const scrollWrapperStyle = {
	height: "100vh",
	overflowY: "auto" as const,
};

export default withErrorBoundary(TermsOfService);
